import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, UserPlus, Trash2, ShieldCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { WorkspaceNav } from "@/components/WorkspaceNav";
import { TEAM } from "@/constants/testIds";
import { useWorkspace } from "@/context/WorkspaceContext";
import { useSession } from "@/context/AuthContext";
import api from "@/lib/api";

const ROLES = ["owner", "admin", "analyst", "viewer"];
const ROLE_DESC = {
  owner: "Full control, billing & ownership transfer",
  admin: "Manage members & connect sources",
  analyst: "Edit glossary & relationships",
  viewer: "Read-only access",
};

export default function Team() {
  const { active } = useWorkspace();
  const { user } = useSession();
  const [members, setMembers] = useState(null);
  const [email, setEmail] = useState("");
  const [role, setRole] = useState("viewer");
  const [adding, setAdding] = useState(false);

  const canManage = active && (active.role === "owner" || active.role === "admin");

  const load = useCallback(async () => {
    if (!active) return;
    const { data } = await api.get(`/workspaces/${active.id}/members`);
    setMembers(data);
  }, [active]);

  useEffect(() => {
    load();
  }, [load]);

  const onAdd = async (e) => {
    e.preventDefault();
    setAdding(true);
    try {
      await api.post(`/workspaces/${active.id}/members`, { email, role });
      toast.success(`Added ${email} as ${role}`);
      setEmail("");
      setRole("viewer");
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to add member");
    } finally {
      setAdding(false);
    }
  };

  const changeRole = async (userId, newRole) => {
    try {
      await api.patch(`/workspaces/${active.id}/members/${userId}`, { role: newRole });
      toast.success("Role updated");
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to update role");
    }
  };

  const removeMember = async (userId) => {
    try {
      await api.delete(`/workspaces/${active.id}/members/${userId}`);
      toast.success("Member removed");
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to remove member");
    }
  };

  return (
    <div data-testid={TEAM.root} className="min-h-screen bg-background text-foreground">
      <WorkspaceNav />
      <main className="mx-auto w-full max-w-4xl px-6 py-10">
        <h1 className="text-2xl font-bold tracking-tight sm:text-3xl">Team</h1>
        <p className="mt-2 text-sm text-muted-foreground">
          Members of <span className="text-foreground">{active?.name}</span> and their roles.
        </p>

        {canManage && (
          <form onSubmit={onAdd} className="mt-8 flex flex-wrap items-end gap-3 rounded-xl border border-border/60 bg-card/40 p-4">
            <div className="flex-1 space-y-1.5">
              <Label className="text-[11px] uppercase tracking-[0.14em] text-muted-foreground">Invite by email</Label>
              <Input
                type="email"
                required
                data-testid={TEAM.addEmail}
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="teammate@company.com"
                className="h-9 bg-background/60 text-sm"
              />
            </div>
            <div className="space-y-1.5">
              <Label className="text-[11px] uppercase tracking-[0.14em] text-muted-foreground">Role</Label>
              <Select value={role} onValueChange={setRole}>
                <SelectTrigger data-testid={TEAM.addRole} className="h-9 w-[140px] text-xs">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {ROLES.map((r) => (
                    <SelectItem key={r} value={r}>{r}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <Button
              type="submit"
              disabled={adding}
              data-testid={TEAM.addSubmit}
              className="h-9 rounded-full bg-accent text-xs uppercase tracking-[0.16em] text-accent-foreground hover:bg-accent/90"
            >
              {adding ? <Loader2 className="h-4 w-4 animate-spin" /> : <><UserPlus className="mr-2 h-4 w-4" />Add</>}
            </Button>
          </form>
        )}

        <p className="mt-3 text-[11px] text-muted-foreground">
          Members must have a Sema account first — they can sign up at <span className="text-accent">/signup</span>.
        </p>

        <div className="mt-6 overflow-hidden rounded-xl border border-border/60">
          {members === null ? (
            <div className="flex justify-center py-12"><Loader2 className="h-5 w-5 animate-spin text-accent" /></div>
          ) : (
            members.map((m) => (
              <div
                key={m.user_id}
                data-testid={TEAM.memberRow}
                className="flex items-center justify-between gap-3 border-b border-border/40 px-4 py-3 last:border-0"
              >
                <div className="min-w-0">
                  <div className="flex items-center gap-2 text-sm font-medium">
                    {m.name || m.email}
                    {m.user_id === user?.id && <span className="text-[10px] text-accent">you</span>}
                  </div>
                  <div className="truncate text-xs text-muted-foreground">{m.email} · {ROLE_DESC[m.role]}</div>
                </div>
                <div className="flex items-center gap-2">
                  {canManage ? (
                    <Select value={m.role} onValueChange={(v) => changeRole(m.user_id, v)}>
                      <SelectTrigger data-testid={TEAM.roleSelect} className="h-8 w-[120px] text-xs">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {ROLES.map((r) => (
                          <SelectItem key={r} value={r}>{r}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  ) : (
                    <span className="inline-flex items-center gap-1 rounded-full border border-border/60 px-2 py-0.5 text-[10px] uppercase tracking-[0.14em] text-muted-foreground">
                      <ShieldCheck className="h-3 w-3" />
                      {m.role}
                    </span>
                  )}
                  {canManage && m.user_id !== user?.id && (
                    <Button
                      variant="ghost"
                      size="icon"
                      data-testid={TEAM.removeBtn}
                      onClick={() => removeMember(m.user_id)}
                      className="h-8 w-8 text-muted-foreground hover:text-red-400"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  )}
                </div>
              </div>
            ))
          )}
        </div>
      </main>
    </div>
  );
}
